//// Application Packages
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import {
    subtractValuesAdditionalConditionalForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    deleteFormData,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import AdditionalLicensedPracticalNursingEndorsementApplicantBackgroundForm from '../../components/licensed-practical-nursing-by-endorsement-components/additional-applicant-background-applicant-component'
import AdditionalLicensedPracticalNursingEndorsementLicenseHolderBackgroundForm from '../../components/licensed-practical-nursing-by-endorsement-components/additional-applicant-background-license-holder-component'

//// Carbon Components
import { Form, TextInput, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const LicensedPracticalNursingExaminationApplicantBackgroundForm = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']
    const conditions = settingsData?.['form']?.['conditions']

    //////// Search Params

    //////// Methods

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (
                (formsData?.[digital_name]?.values?.APP_RN === undefined ||
                    formsData?.[digital_name]?.values?.APP_RN === 'N') &&
                (formsData?.[digital_name]?.values?.APP_LPN === undefined ||
                    formsData?.[digital_name]?.values?.APP_LPN === 'N')
            ) {
                schema.yup = object({
                    NRSE_NAME: string().min(0).max(40),
                    APP_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_LPN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    NRSE_NAME: false,
                    NRSE_NAMEErrMsg: '',
                    APP_RN: false,
                    APP_RNErrMsg: '',
                    APP_LPN: false,
                    APP_LPNErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.APP_RN === 'Y' &&
                (formsData?.[digital_name]?.values?.APP_LPN === undefined ||
                    formsData?.[digital_name]?.values?.APP_LPN === 'N')
            ) {
                schema.yup = object({
                    NRSE_NAME: string().min(0).max(40),
                    APP_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_LPN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_METH_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LIC_TYPE_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_RN_DTE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    NRSE_NAME: false,
                    NRSE_NAMEErrMsg: '',
                    APP_RN: false,
                    APP_RNErrMsg: '',
                    APP_LPN: false,
                    APP_LPNErrMsg: '',
                    APP_METH_RN: false,
                    APP_METH_RNErrMsg: '',
                    LIC_TYPE_RN: false,
                    LIC_TYPE_RNErrMsg: '',
                    APP_RN_DTE: false,
                    APP_RN_DTEErrMsg: '',
                }
            } else if (
                (formsData?.[digital_name]?.values?.APP_RN === undefined ||
                    formsData?.[digital_name]?.values?.APP_RN === 'N') &&
                formsData?.[digital_name]?.values?.APP_LPN === 'Y'
            ) {
                schema.yup = object({
                    NRSE_NAME: string().min(0).max(40),
                    APP_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_LPN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LIC_TYPE_LPN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_LPN_DTE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    NRSE_NAME: false,
                    NRSE_NAMEErrMsg: '',
                    APP_RN: false,
                    APP_RNErrMsg: '',
                    APP_LPN: false,
                    APP_LPNErrMsg: '',
                    LIC_TYPE_LPN: false,
                    LIC_TYPE_LPNErrMsg: '',
                    APP_LPN_DTE: false,
                    APP_LPN_DTEErrMsg: '',
                }
            } else if (
                formsData?.[digital_name]?.values?.APP_LPN === 'Y' &&
                formsData?.[digital_name]?.values?.APP_RN === 'Y'
            ) {
                schema.yup = object({
                    NRSE_NAME: string().min(0).max(40),
                    APP_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_LPN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_METH_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LIC_TYPE_RN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_RN_DTE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    LIC_TYPE_LPN: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    APP_LPN_DTE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                })
                schema.valid = {
                    NRSE_NAME: false,
                    NRSE_NAMEErrMsg: '',
                    APP_RN: false,
                    APP_RNErrMsg: '',
                    APP_LPN: false,
                    APP_LPNErrMsg: '',
                    APP_LPNApplMthd: false,
                    APP_LPNApplMthdErrMsg: '',
                    LIC_TYPE_LPN: false,
                    LIC_TYPE_LPNErrMsg: '',
                    APP_LPN_DTE: false,
                    APP_LPN_DTEErrMsg: '',
                    APP_METH_RN: false,
                    APP_METH_RNErrMsg: '',
                    LIC_TYPE_RN: false,
                    LIC_TYPE_RNErrMsg: '',
                    APP_RN_DTE: false,
                    APP_RN_DTEErrMsg: '',
                }
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Removal Of Additional Applicant Background Component/s
    const handleSubtractAdditionalConditionalComponent = (e) => {
        try {
            if (e?.target?.name && e?.target?.value) {
                const values = subtractValuesAdditionalConditionalForm(
                    conditions,
                    formsData?.[digital_name]?.values,
                    e?.target?.name,
                    e?.target?.value
                )
                if (values) {
                    dispatch(
                        deleteFormData({
                            digital_name: digital_name,
                        })
                    )
                    dispatch(
                        updateFormData({
                            digital_name: digital_name,
                            values: values,
                        })
                    )
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Conditional Applicant Background Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Applicant Background: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Applicant Background: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Applicant Background: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Applicant Background Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <TextInput
                        className={''}
                        labelText="If you have previously been licensed, what name did you use when you were first licensed?"
                        id="NRSE_NAME"
                        name="NRSE_NAME"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.NRSE_NAME}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.NRSE_NAME}
                        invalidText={
                            formsData?.[digital_name]?.errors?.NRSE_NAMEErrMsg
                        }
                    />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.APP_RN
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.APP_RN
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.APP_RN
                        }
                        legendText="Have you ever applied for nursing licensure in Florida?"
                        name="APP_RN"
                        invalid={formsData?.[digital_name]?.errors?.APP_RN}
                        invalidText={
                            formsData?.[digital_name]?.errors?.APP_RNErrMsg
                        }
                    >
                        <RadioButton
                            id="APP_RNYes"
                            name="APP_RNYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="APP_RNNo"
                            name="APP_RNNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalLicensedPracticalNursingEndorsementApplicantBackgroundForm />

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.APP_LPN
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.APP_LPN
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.APP_LPN
                        }
                        legendText="Have you ever held a nursing license in Florida?"
                        name="APP_LPN"
                        invalid={formsData?.[digital_name]?.errors?.APP_LPN}
                        invalidText={
                            formsData?.[digital_name]?.errors?.APP_LPNErrMsg
                        }
                    >
                        <RadioButton
                            id="APP_LPNYes"
                            name="APP_LPNYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                        <RadioButton
                            id="APP_LPNNo"
                            name="APP_LPNNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={(e) => {
                                handleData(e)
                                handleSubtractAdditionalConditionalComponent(e)
                            }}
                        />
                    </RadioButtonGroup>

                    <AdditionalLicensedPracticalNursingEndorsementLicenseHolderBackgroundForm />

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default LicensedPracticalNursingExaminationApplicantBackgroundForm
